<template>
    <section v-if="allAttachments.length" aria-labelledby="attachments-title" class="review-section">
        <div class="section-heading align-start">
            <div>
                <h2 id="attachments-title" class="subtitle-1 font-weight-bold">Allegati ed evidenze 8D</h2>
                <div class="caption grey--text text--darken-1">
                    Le miniature sono visibili subito; apri l’anteprima per ingrandire e scegli cosa aggiungere al rapporto.
                </div>
            </div>
            <div class="d-flex flex-wrap justify-end attachment-tools">
                <v-btn x-small outlined color="error" :disabled="!unselectedVisualIds.length" @click="selectAllVisual">
                    <v-icon x-small left>mdi-checkbox-multiple-marked-outline</v-icon>Includi tutte
                </v-btn>
                <v-btn x-small text color="grey darken-1" :disabled="!selectedVisualCount" @click="clearVisual">
                    Escludi tutte
                </v-btn>
                <v-chip small outlined color="error">
                    <v-icon small left>mdi-alert-octagon-outline</v-icon>
                    {{ selectedVisualCount }}/{{ visibleVisualAttachments.length }} evidenze NC
                </v-chip>
            </div>
        </div>

        <v-alert v-if="hiddenAttachments.length" dense text type="info" class="mt-3 mb-0">
            <div class="d-flex align-center flex-wrap">
                <div>
                    <strong>{{ hiddenAttachments.length }} allegati tecnici nascosti</strong>
                    <span class="caption ml-1">Loghi o immagini di firma esclusi dalle proposte e dall’8D.</span>
                </div>
                <v-spacer />
                <v-btn x-small text color="primary" @click="showHidden = !showHidden">
                    <v-icon x-small left>{{ showHidden ? "mdi-eye-off-outline" : "mdi-eye-outline" }}</v-icon>
                    {{ showHidden ? "Nascondi" : "Controlla" }}
                </v-btn>
            </div>
        </v-alert>

        <v-row dense class="mt-3">
            <v-col v-for="attachment in displayedAttachments" :key="attachment.id" cols="12" lg="6">
                <v-card outlined :class="['attachment-card fill-height', { 'attachment-card--hidden': attachment.hidden_by_default }]">
                    <v-card-text class="pb-2">
                        <div class="d-flex align-start">
                            <v-icon :color="isVisual(attachment) ? 'primary' : 'grey'" class="mr-3">
                                {{ attachmentIcon(attachment) }}
                            </v-icon>
                            <div class="min-width-0 flex-grow-1">
                                <div class="subtitle-2 text-truncate">{{ attachment.filename }}</div>
                                <div class="caption grey--text">{{ sizeLabel(attachment.size) }}</div>
                                <div v-if="attachment.classification_reason" class="caption grey--text text--darken-1 mt-1">
                                    {{ attachment.classification_reason }}
                                </div>
                            </div>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        icon
                                        small
                                        color="primary"
                                        v-bind="attrs"
                                        v-on="on"
                                        :aria-label="`Apri anteprima di ${attachment.filename}`"
                                        @click="preview(attachment)"
                                    >
                                        <v-icon>mdi-eye-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>Apri anteprima</span>
                            </v-tooltip>
                        </div>
                        <mail-attachment-thumbnail
                            v-if="isVisual(attachment)"
                            :message-uid="message.uid"
                            :attachment="attachment"
                            @open="preview(attachment)"
                        />
                        <v-chip v-if="isSelected(attachment.id)" x-small color="error" class="mt-3">
                            PEZZO NON CONFORME · incluso nell’8D
                        </v-chip>
                        <v-chip v-else-if="attachment.hidden_by_default" x-small outlined color="grey" class="mt-3">
                            LOGO / FIRMA · escluso automaticamente
                        </v-chip>
                    </v-card-text>
                    <v-card-actions>
                        <v-menu offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn x-small text color="primary" v-bind="attrs" v-on="on">
                                    <v-icon x-small left>mdi-tag-check-outline</v-icon>Classifica
                                </v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item @click="classify(attachment, 'evidence')">
                                    <v-list-item-title>È un’evidenza utile</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="classify(attachment, 'signature')">
                                    <v-list-item-title>È un logo o una firma</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <v-spacer />
                        <v-switch
                            :input-value="isSelected(attachment.id)"
                            :disabled="!isVisual(attachment)"
                            dense
                            hide-details
                            color="error"
                            :aria-label="`Includi ${attachment.filename} nell'8D`"
                            @change="toggle(attachment.id, $event)"
                        />
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-alert dense text type="info" class="mt-3 mb-0">
            Solo foto e PDF selezionati saranno accodati al PDF 8D. Ogni pagina aggiunta verrà marcata
            <strong>“PEZZO NON CONFORME”</strong>; il file originale della mail non viene modificato.
        </v-alert>

        <v-dialog v-model="dialog" max-width="1100" @input="onDialogChange">
            <v-card>
                <v-card-title class="preview-title">
                    <div class="min-width-0">
                        <div class="overline error--text">Evidenza della non conformità</div>
                        <div class="subtitle-1 text-truncate">{{ activeAttachment && activeAttachment.filename }}</div>
                    </div>
                    <v-spacer />
                    <v-btn icon aria-label="Chiudi anteprima" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pa-0 preview-area">
                    <v-progress-linear v-if="loading" indeterminate color="primary" />
                    <div v-if="previewUrl && isImage(activeAttachment)" class="image-preview-wrap">
                        <div class="nonconforming-banner">PEZZO NON CONFORME</div>
                        <img :src="previewUrl" :alt="activeAttachment.filename" class="image-preview" />
                    </div>
                    <div v-else-if="previewUrl && isPdf(activeAttachment)" class="pdf-preview-wrap">
                        <div class="nonconforming-banner">EVIDENZA SEGNALAZIONE · PEZZO NON CONFORME</div>
                        <iframe :src="previewUrl" title="Anteprima PDF" class="pdf-preview" />
                    </div>
                    <v-alert v-else-if="previewError" type="warning" text class="ma-5">{{ previewError }}</v-alert>
                </v-card-text>
                <v-card-actions class="px-5 py-3">
                    <v-btn icon :disabled="!hasPrevious" aria-label="Allegato precedente" @click="showRelative(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
                    <v-btn icon :disabled="!hasNext" aria-label="Allegato successivo" @click="showRelative(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
                    <v-btn text color="primary" :disabled="!previewUrl" @click="downloadActive">
                        <v-icon left>mdi-download</v-icon>Scarica originale
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        v-if="activeAttachment && isVisual(activeAttachment)"
                        :color="isSelected(activeAttachment.id) ? 'grey' : 'error'"
                        :outlined="isSelected(activeAttachment.id)"
                        @click="toggle(activeAttachment.id, !isSelected(activeAttachment.id))"
                    >
                        {{ isSelected(activeAttachment.id) ? "Escludi dall’8D" : "Includi nell’8D come NC" }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </section>
</template>

<script>
import MailAttachmentThumbnail from "./MailAttachmentThumbnail.vue";

export default {
    name: "MailSegnalazioneAttachments",
    components: { MailAttachmentThumbnail },
    props: {
        message: { type: Object, required: true },
        selectedIds: { type: Array, default: () => [] },
    },
    data: () => ({
        activeAttachment: null,
        dialog: false,
        loading: false,
        previewError: "",
        previewUrl: "",
        showHidden: false,
    }),
    computed: {
        allAttachments() {
            return this.message.attachments || [];
        },
        visibleAttachments() {
            return this.allAttachments.filter((attachment) => !attachment.hidden_by_default);
        },
        hiddenAttachments() {
            return this.allAttachments.filter((attachment) => attachment.hidden_by_default);
        },
        displayedAttachments() {
            return this.showHidden ? this.allAttachments : this.visibleAttachments;
        },
        visibleVisualAttachments() {
            return this.visibleAttachments.filter((attachment) => this.isVisual(attachment));
        },
        navigableAttachments() {
            return this.displayedAttachments.filter((attachment) => this.isVisual(attachment));
        },
        selectedVisualCount() {
            return this.visibleVisualAttachments.filter((attachment) => this.isSelected(attachment.id)).length;
        },
        unselectedVisualIds() {
            return this.visibleVisualAttachments.filter((attachment) => !this.isSelected(attachment.id)).map((attachment) => attachment.id);
        },
        activeIndex() {
            return this.navigableAttachments.findIndex((attachment) => attachment.id === this.activeAttachment?.id);
        },
        hasPrevious() { return this.activeIndex > 0; },
        hasNext() { return this.activeIndex >= 0 && this.activeIndex < this.navigableAttachments.length - 1; },
    },
    beforeDestroy() {
        this.releasePreview();
    },
    methods: {
        isPdf(attachment) {
            return attachment?.content_type === "application/pdf";
        },
        isImage(attachment) {
            return Boolean(attachment?.content_type?.startsWith("image/"));
        },
        isVisual(attachment) {
            return this.isPdf(attachment) || this.isImage(attachment);
        },
        isSelected(id) {
            return this.selectedIds.includes(id);
        },
        attachmentIcon(attachment) {
            if (this.isPdf(attachment)) return "mdi-file-pdf-box";
            if (this.isImage(attachment)) return "mdi-file-image-outline";
            return "mdi-file-outline";
        },
        sizeLabel(value) {
            const size = Number(value || 0);
            if (size < 1024) return `${size} B`;
            if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
            return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        },
        toggle(id, enabled) {
            const selected = new Set(this.selectedIds);
            if (enabled) selected.add(id);
            else selected.delete(id);
            this.$emit("update:selected-ids", [...selected]);
        },
        classify(attachment, classification) {
            this.$emit("classification-feedback", {
                kind: "attachment",
                predicted_value: attachment.classification || "",
                corrected_value: classification,
                accepted: attachment.classification === classification,
                context: {
                    filename: attachment.filename,
                    content_type: attachment.content_type,
                },
            });
        },
        selectAllVisual() {
            this.$emit("update:selected-ids", [...new Set([...this.selectedIds, ...this.visibleVisualAttachments.map((item) => item.id)])]);
        },
        clearVisual() {
            const visualIds = new Set(this.allAttachments.filter((item) => this.isVisual(item)).map((item) => item.id));
            this.$emit("update:selected-ids", this.selectedIds.filter((id) => !visualIds.has(id)));
        },
        showRelative(offset) {
            const attachment = this.navigableAttachments[this.activeIndex + offset];
            if (attachment) this.preview(attachment);
        },
        downloadActive() {
            if (!this.previewUrl || !this.activeAttachment) return;
            const link = document.createElement("a");
            link.href = this.previewUrl;
            link.download = this.activeAttachment.filename;
            link.click();
        },
        async preview(attachment) {
            this.releasePreview();
            this.activeAttachment = attachment;
            this.dialog = true;
            this.previewError = "";
            if (!this.isVisual(attachment)) {
                this.previewError = "Anteprima non disponibile per questo formato.";
                return;
            }
            if (String(this.message.uid).startsWith("demo-")) {
                this.previewError = "L’anteprima sarà disponibile con una mail reale.";
                return;
            }
            this.loading = true;
            try {
                const response = await this.$work.get(
                    `/qualita/mail-segnalazioni/${encodeURIComponent(this.message.uid)}/allegati/${encodeURIComponent(attachment.id)}/`,
                    {},
                    false,
                    null,
                    { responseType: "blob" }
                );
                this.previewUrl = URL.createObjectURL(response.data);
            } catch (_error) {
                this.previewError = "Impossibile caricare l’anteprima dell’allegato.";
            } finally {
                this.loading = false;
            }
        },
        releasePreview() {
            if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = "";
        },
        onDialogChange(open) {
            if (!open) this.releasePreview();
        },
    },
};
</script>

<style scoped>
.review-section { padding: 20px 0; }
.section-heading { display: flex; justify-content: space-between; gap: 16px; }
.attachment-card { display: flex; flex-direction: column; justify-content: space-between; }
.attachment-card--hidden { opacity: 0.72; border-style: dashed !important; }
.preview-title { border-bottom: 1px solid rgba(0, 0, 0, 0.08); }
.preview-area { min-height: 320px; background: #263238; }
.image-preview-wrap, .pdf-preview-wrap { position: relative; min-height: 320px; }
.image-preview-wrap { display: flex; justify-content: center; align-items: center; padding: 72px 20px 20px; }
.image-preview { max-width: 100%; max-height: 72vh; object-fit: contain; }
.pdf-preview { width: 100%; height: 72vh; border: 0; background: white; }
.nonconforming-banner {
    position: absolute; z-index: 2; top: 0; left: 0; right: 0; padding: 14px;
    color: white; background: #b71c1c; text-align: center; font-weight: 800; letter-spacing: 0.08em;
}
.min-width-0 { min-width: 0; }
.attachment-tools { gap: 6px; }
@media (max-width: 599px) {
    .section-heading { flex-direction: column; gap: 8px; }
    .pdf-preview { height: 60vh; }
}
</style>
